import React, { useEffect, useState } from "react";
import Card from "../../../../components/card";
import { NewsCategory, NewsPost, NewsPostRequest } from "../../../../interfaces/News";
import { NEWS_CATEGORIES } from "../categories";

interface Props {
  editing: NewsPost | null;
  saving: boolean;
  onSubmit: (req: NewsPostRequest, publish: boolean) => void;
  onCancel: () => void;
}

const input =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all " +
  "focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 " +
  "dark:border-white/10 dark:bg-navy-900 dark:text-white";
const label = "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

/** Formulário de publicação. Rascunho e publicação são o mesmo formulário, dois botões (design D7). */
const NewsComposer: React.FC<Props> = ({ editing, saving, onSubmit, onCancel }) => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<NewsCategory>(NewsCategory.Comunicado);
  const [pinned, setPinned] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(editing?.title ?? "");
    setSummary(editing?.summary ?? "");
    setBody(editing?.body ?? "");
    setCategory(editing?.category ?? NewsCategory.Comunicado);
    setPinned(editing?.pinned ?? false);
    setExpiresAt(editing?.expiresAt ? editing.expiresAt.slice(0, 10) : "");
    setError(null);
  }, [editing]);

  const submit = (publish: boolean) => {
    if (title.trim().length < 3 || body.trim().length === 0) {
      setError("Título (3+ caracteres) e corpo são obrigatórios.");
      return;
    }
    setError(null);
    onSubmit(
      {
        title: title.trim(),
        summary: summary.trim() || null,
        body,
        category,
        pinned,
        publish,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      },
      publish
    );
  };

  return (
    <Card extra="p-5 gap-4">
      <h2 className="font-display text-lg font-bold tracking-tight text-navy-700 dark:text-white">
        {editing ? "Editar comunicado" : "Novo comunicado"}
      </h2>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div>
        <label className={label} htmlFor="news-title">
          Título *
        </label>
        <input
          id="news-title"
          className={input}
          value={title}
          maxLength={160}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="O que mudou, em uma frase"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="news-category">
            Editoria
          </label>
          <select
            id="news-category"
            className={input}
            value={category}
            onChange={(e) => setCategory(Number(e.target.value) as NewsCategory)}
          >
            {NEWS_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label} — {c.hint}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={label} htmlFor="news-expires">
            Expira em (opcional)
          </label>
          <input
            id="news-expires"
            type="date"
            className={input}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={label} htmlFor="news-summary">
          Chamada (opcional)
        </label>
        <input
          id="news-summary"
          className={input}
          value={summary}
          maxLength={300}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="A linha que aparece no feed antes de abrir"
        />
      </div>

      <div>
        <label className={label} htmlFor="news-body">
          Corpo *
        </label>
        <textarea
          id="news-body"
          className={`${input} min-h-[180px] resize-y text-[13px]`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escreva o comunicado. As quebras de linha são preservadas."
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Texto simples: aparece no portal e no widget exatamente como você escrever.
        </p>
      </div>

      <label className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={pinned}
          onChange={(e) => setPinned(e.target.checked)}
          className="h-4 w-4 accent-dv-blue-500"
        />
        Fixar no topo (não expira com a janela de 60 dias)
      </label>

      <div className="flex flex-wrap items-center justify-end gap-2.5">
        {editing && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          disabled={saving}
          onClick={() => submit(false)}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
        >
          Salvar rascunho
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => submit(true)}
          className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:opacity-50"
        >
          {saving ? "Enviando…" : "Publicar para a empresa"}
        </button>
      </div>
    </Card>
  );
};

export default NewsComposer;
